import { ArrowBack, Close, MoreHoriz } from '@mui/icons-material';
import { Box, ClickAwayListener, IconButton, Stack, type Theme, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { objectEntries } from '/@/utils';
import { reverse } from '/@/utils/router';
import CreateRecommendationModal from '/@/views/dashboard/rv/components/create-recommendation-modal';
import IconNotification from '../../notification/icon-notification';
import ProfileIcon from '../components/profile-icon';
import { type NavbarTabKey, useTabs, useUtilitiesTabs } from '../hooks';
import { SidebarButton } from '../sidebar';

export const BOTTOM_BAR_WIDTH = '216px';
export const BOTTOM_BAR_HEIGHT = '64px';

const SidebarStack = ({ children }: { children: React.ReactNode }) => {
    return (
        <Stack
            sx={{
                width: BOTTOM_BAR_WIDTH,
                py: 2,
                px: 3,
                position: 'fixed',
                bottom: BOTTOM_BAR_HEIGHT,
                right: 0,
                zIndex: 10,
                height: `calc(100% - ${BOTTOM_BAR_HEIGHT})`,
                background: 'white',
                borderLeft: '1px solid #10384F1F;',
            }}
            spacing={2}
        >
            {children}
        </Stack>
    );
};

export const BottomBarItem = ({
    key,
    tab,
    isSelected,
    onClick,
}: {
    key: string;
    tab: { label: string; icon: React.ReactNode; onClick: (e: React.MouseEvent<HTMLElement>) => void };
    isSelected: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
    return (
        <Stack
            key={key}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
                tab.onClick(e);
                onClick?.(e);
            }}
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            py={0.5}
            sx={{
                width: '100%',
                cursor: 'pointer',
                backgroundColor: isSelected ? 'action.hover' : 'transparent',
                textAlign: 'end',

                '& svg': {
                    fill: (t: Theme) => t.palette.primary.main,
                    width: '16px !important',
                    height: '16px !important',
                    m: 0.5,
                },
            }}
        >
            {tab.icon}

            <Typography variant='caption' color='primary' fontWeight={isSelected ? 'bold' : 'normal'}>
                {tab.label}
            </Typography>
        </Stack>
    );
};

const BottomBar = ({ selectedTab }: { selectedTab?: NavbarTabKey }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [utilitiesOpen, setUtilitiesOpen] = useState(false);

    const [createRecommendationModalOpen, setCreateRecommendationModalOpen] = useState(false);
    const tabs = useTabs({
        setUtilitiesOpen,
        setOpen,
    });
    const utilitiesTabs = useUtilitiesTabs({
        setCreateRecommendationModalOpen,
    });

    return (
        <>
            <ClickAwayListener
                onClickAway={() => {
                    setOpen(false);
                    setUtilitiesOpen(false);
                }}
            >
                <Box sx={{ zIndex: 10000 }}>
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        sx={{
                            height: BOTTOM_BAR_HEIGHT,
                            position: 'fixed',
                            width: '100%',
                            background: 'white',
                            borderTop: '1px solid #10384F1F;',
                            bottom: 0,
                            left: 0,

                            pl: 3,
                            pr: 1.5,
                            py: 1,
                        }}
                    >
                        <Box
                            sx={{ width: '40px', height: '40px', cursor: 'pointer' }}
                            component='img'
                            src={settings.app.favicon}
                            alt='logo'
                            onClick={() => {
                                navigate(reverse('NewHome'));
                            }}
                        />
                        <IconButton
                            onClick={() => {
                                setOpen(!open);
                                setUtilitiesOpen(false);
                            }}
                        >
                            {open ? <Close /> : <MoreHoriz />}
                        </IconButton>
                    </Stack>
                    {open &&
                        (!utilitiesOpen ? (
                            <SidebarStack>
                                <Typography variant='h6' color='primary'>
                                    {_('Menu')}
                                </Typography>
                                <Stack spacing={0.5}>
                                    {objectEntries(tabs).map(([key, tab]) => {
                                        return <BottomBarItem key={key} tab={tab} isSelected={selectedTab === key} />;
                                    })}
                                </Stack>
                                <Stack
                                    direction='row'
                                    justifyContent='space-between'
                                    alignItems='center'
                                    sx={{ mt: 'auto !important' }}
                                >
                                    <SidebarButton
                                        key={'profile'}
                                        tab={{
                                            label: _('Perfil'),
                                            icon: <ProfileIcon />,
                                            onClick: () => {
                                                setOpen(false);
                                                setUtilitiesOpen(false);
                                                navigate(reverse('Profile'));
                                            },
                                        }}
                                        isSelected={false}
                                        sx={{ width: 'auto' }}
                                        direction='row'
                                    />
                                    <IconNotification variant='bottombar' />
                                </Stack>
                            </SidebarStack>
                        ) : (
                            <SidebarStack>
                                <Stack
                                    direction='row'
                                    alignItems='center'
                                    spacing={1}
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => setUtilitiesOpen(false)}
                                >
                                    <ArrowBack />
                                    <Typography variant='h6' color='primary'>
                                        {_('Utilidades')}
                                    </Typography>
                                </Stack>
                                <Stack spacing={0.5}>
                                    {objectEntries(utilitiesTabs)
                                        .filter(([, tab]) => tab.enabled)
                                        .map(([key, tab]) => {
                                            return (
                                                <BottomBarItem
                                                    key={key}
                                                    tab={tab}
                                                    isSelected={selectedTab === key}
                                                    onClick={() => {
                                                        setOpen(false);
                                                        setUtilitiesOpen(false);
                                                    }}
                                                />
                                            );
                                        })}
                                </Stack>
                            </SidebarStack>
                        ))}
                </Box>
            </ClickAwayListener>
            <CreateRecommendationModal
                open={createRecommendationModalOpen}
                setOpen={setCreateRecommendationModalOpen}
                entrySource='app-navbar'
            />
        </>
    );
};

export default BottomBar;
